import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand, faMinimize } from "@fortawesome/free-solid-svg-icons";
// AUTHORIZATION
// REDUX
import { updateMapViewPinArray } from "../../../redux/mapView/mapViewSlice";
import MonitoringMapViewList from "./MonitoringMapViewList";
import MonitoringMapViewWrapper from "./MonitoringMapViewWrapper";
import SvgController from "../../mapViewManagement/mapEditor/SvgController";
import { extractMapRenderData, resolveMapCoordinate } from "./mapRenderData";
import MapDisplayLegend from "./MapDisplayLegend";

export function resolvePublicAssetUrl(path: string): string {
  if (!path) {
    return path;
  }

  if (/^(https?:)?\/\//i.test(path) || path.startsWith("data:")) {
    return path;
  }

  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  return `${base}${normalizedPath}`;
}

function safeJsonStringify(value: unknown): string {
  const seen = new WeakSet<object>();

  try {
    const json = JSON.stringify(
      value,
      (_key, currentValue) => {
        if (typeof currentValue === "bigint") {
          return currentValue.toString();
        }

        if (currentValue instanceof Uint8Array) {
          return Array.from(currentValue);
        }

        if (currentValue && typeof currentValue === "object") {
          if (seen.has(currentValue)) {
            return "[Circular]";
          }
          seen.add(currentValue);
        }

        return currentValue;
      },
      2,
    );

    return json ?? "null";
  } catch {
    return JSON.stringify({ error: "Unable to serialize map data" });
  }
}

function MonitoringMapView({ layouts }: { layouts: any }) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [svgSrc, setSvgSrc] = useState<string>("");
  const mapViewportRef = useRef<HTMLDivElement | null>(null);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { mapViewItem, mapViewArray, mapViewPinArray } = useSelector((state: any) => state.mapView);
  const showDisplayLegend = Boolean(
    mapViewItem?.has_display_view ?? mapViewItem?.hasDisplayView ?? mapViewItem?.main ?? false,
  );
  const { groupsAndBanks, liveLifts } = useMemo(
    () => extractMapRenderData(mapViewItem),
    [mapViewItem],
  );

  const requestMapFullscreen = useCallback(async () => {
    const element = mapViewportRef.current;
    if (!element) {
      return;
    }

    const isCurrentFullscreenTarget =
      document.fullscreenElement === element ||
      (document as any).webkitFullscreenElement === element ||
      (document as any).msFullscreenElement === element;

    if (isCurrentFullscreenTarget) {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        await (document as any).msExitFullscreen();
      }
      return;
    }

    const requestFullscreen = (target: HTMLElement) => {
      if (typeof target.requestFullscreen === "function") {
        return target.requestFullscreen();
      }
      if (typeof (target as any).webkitRequestFullscreen === "function") {
        return (target as any).webkitRequestFullscreen();
      }
      if (typeof (target as any).msRequestFullscreen === "function") {
        return (target as any).msRequestFullscreen();
      }
      return Promise.resolve();
    };

    try {
      await requestFullscreen(element);
    } catch {
      setIsMapFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const element = mapViewportRef.current;
      const isFullscreen =
        document.fullscreenElement === element ||
        (document as any).webkitFullscreenElement === element ||
        (document as any).msFullscreenElement === element;
      setIsMapFullscreen(isFullscreen);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("msfullscreenchange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const svgUrl = resolvePublicAssetUrl(
      mapViewItem?.map_img_url || mapViewItem?.mapImgUrl || mapViewItem?.offline_url || "",
    );
    setSvgSrc((previous) => (previous === svgUrl ? previous : svgUrl));

    const nextPins = liveLifts
      .filter((pin) => pin.x !== null && pin.y !== null)
      .map((pin) => ({
        id: pin.id,
        text: pin.text,
        type: pin.type,
        x: pin.x as number,
        y: pin.y as number,
        direction: pin.direction,
      }));

    const nextPinSignature = JSON.stringify(
      nextPins.map((pin) => ({
        id: pin.id,
        text: pin.text,
        type: pin.type,
        x: Number(pin.x),
        y: Number(pin.y),
        direction: pin.direction,
      })),
    );
    const currentPinSignature = JSON.stringify(
      (Array.isArray(mapViewPinArray) ? mapViewPinArray : []).map((pin: any) => ({
        id: pin.id,
        text: pin.text,
        type: pin.type,
        x: Number(pin.x),
        y: Number(pin.y),
        direction: pin.direction,
      })),
    );

    if (nextPinSignature !== currentPinSignature) {
      dispatch(updateMapViewPinArray(nextPins));
    }
  }, [dispatch, liveLifts, mapViewItem, mapViewPinArray]);

  if (!mapViewArray?.length) return <>Select one organization</>;
  return (
    <>
      <Row style={{ width: "100%", marginTop: "0.5rem", marginBottom: "0.5rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontWeight: 500, fontSize: 14, lineHeight: "22px" }}>Map:</span>
          <MonitoringMapViewList />
        </div>
      </Row>
      {/** BODY */}
      <Row
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "1rem",
          marginBottom: "1rem",
          width: "100%",
        }}
      >
        <div
          ref={mapViewportRef}
          style={{
            position: "relative",
            width: "100%",
            height: "70vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "transparent",
            overflow: "hidden",
            contain: "layout style paint",
          }}
        >
          <button
            type="button"
            onClick={requestMapFullscreen}
            aria-label={isMapFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 2600,
              border: "none",
              borderRadius: 999,
              width: 34,
              height: 34,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(15, 23, 42, 0.7)",
              color: "#ffffff",
              cursor: "pointer",
              boxShadow: "0 8px 20px rgba(0, 0, 0, 0.22)",
            }}
          >
            <FontAwesomeIcon icon={isMapFullscreen ? faMinimize : faExpand} />
          </button>
          <div
            style={{
              width: "100%",
              height: "100%",
              position: "relative",
              minHeight: 0,
            }}
          >
            <SvgController
              svgSrc={svgSrc}
              alt={mapViewItem?.name || "Map view"}
              showControls={!showDisplayLegend}
              minZoom={0.5}
              maxZoom={5}
              zoomStep={0.2}
              onSvgLoaded={() => {
                safeJsonStringify(mapViewItem);
              }}
              waypoints={[]}
              groupWaypoints={groupsAndBanks}
              renderOverlay={({ width, height }) => (
                <>
                  {liveLifts.map((lift) => {
                    const x = resolveMapCoordinate(lift.x, width);
                    const y = resolveMapCoordinate(lift.y, height);
                    if (x === null || y === null) {
                      return null;
                    }

                    return (
                      <MonitoringMapViewWrapper
                        key={lift.id}
                        liftId={lift.id}
                        liftType={lift.liftType}
                        text={lift.text}
                        direction={lift.direction}
                        type={lift.type}
                        x={x}
                        y={y}
                        layouts={layouts}
                        interactive={false}
                        displayName={lift.displayName}
                        scale={lift.scale}
                        backgroundColorHex={lift.backgroundColorHex}
                        backgroundOpacity={lift.backgroundOpacity}
                        titleFontSize={lift.titleFontSize}
                        titleColorHex={lift.titleColorHex}
                        titleRotationDeg={lift.titleRotationDeg}
                        backgroundWidth={lift.backgroundWidth}
                        backgroundHeight={lift.backgroundHeight}
                      />
                    );
                  })}
                </>
              )}
            />
          </div>
          {showDisplayLegend ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <MapDisplayLegend overlay />
            </div>
          ) : null}
        </div>
      </Row>
    </>
  );
}

export default MonitoringMapView;
